/**
 * Webmail portal translations
 */

export const webmail = {
  login: {
    title: 'Webmail Login',
    description: 'Sign in with your email account',
    email: 'Email address',
    password: 'Password',
    submit: 'Sign in',
    success: 'Successfully signed in',
    error: 'Sign in failed',
    backToPortal: 'Back to portal',
    captchaRequired: 'Please complete the captcha.'
  },

  tabs: {
    overview: 'Overview',
    vacation: 'Vacation',
    identities: 'Identities',
    appPasswords: 'App Passwords',
    history: 'Login History'
  },

  dashboard: {
    subtitle: 'Manage your email mailbox',
    loadError: 'Failed to load mailbox'
  },

  delegation: {
    managedDomains: 'Managed Domains',
    managedDomainsHint: 'You have been invited to manage these domains. Click a domain to manage mailboxes, aliases and mailing lists.',
    permissions: 'Permissions',
    permissionCount: '{count} permission | {count} permissions',
    subtitle: 'Manage delegated domain',
    notFound: 'Domain not found',
    managing: 'Managing',
    noAccess: 'You do not have access to this domain',
    backToDashboard: 'Back to Dashboard',
    domainField: 'Domain',

    accounts: {
      title: 'Mail Accounts',
      create: 'Create Mailbox',
      errorLoading: 'Failed to load mail accounts',
      empty: 'No mail accounts found',
      emptyHint: 'Create a mailbox to get started',
      active: 'Active',
      inactive: 'Inactive',
      toggleSuccess: 'Status updated',
      toggleError: 'Failed to update status',
      deleteSuccess: 'Mail account deleted',
      deleteError: 'Failed to delete mail account',
      deleteTitle: 'Delete Mail Account',
      deleteConfirm: 'Delete',
      deleteWarning: 'This action cannot be undone. All emails in this mailbox will be permanently deleted.',
      delete: 'Delete',
      columns: {
        email: 'Email',
        type: 'Type',
        status: 'Status',
        quota: 'Quota',
        actions: 'Actions'
      },
      types: {
        mailbox: 'Mailbox',
        alias: 'Alias',
        list: 'Mailing List',
        shared: 'Shared'
      }
    },

    lists: {
      title: 'Mailing Lists',
      create: 'Create Mailing List',
      errorLoading: 'Failed to load mailing lists',
      empty: 'No mailing lists found',
      emptyHint: 'Create a mailing list to get started',
      active: 'Active',
      inactive: 'Inactive',
      delete: 'Delete',
      createTitle: 'Create Mailing List',
      creating: 'Creating...',
      createSubmit: 'Create',
      createSuccess: 'Mailing list created',
      createError: 'Failed to create mailing list',
      deleteTitle: 'Delete Mailing List',
      deleteConfirm: 'Delete',
      deleteWarning: 'This action cannot be undone. The mailing list will be permanently deleted.',
      deleteSuccess: 'Mailing list deleted',
      deleteError: 'Failed to delete mailing list',
      columns: {
        email: 'Email',
        members: 'Members',
        externalMembers: 'External Members',
        status: 'Status',
        actions: 'Actions'
      },
      fields: {
        name: 'List Name',
        members: 'Members',
        membersPlaceholder: "user{'@'}domain.com\nother{'@'}domain.com",
        membersHint: 'One email address per line, comma or semicolon separated',
        externalMembers: 'External Members',
        externalMembersPlaceholder: "external{'@'}example.com",
        externalMembersHint: 'External email addresses (outside this domain)'
      },
      validation: {
        nameRequired: 'List name is required',
        nameInvalid: 'Only letters, numbers, dots and hyphens allowed',
        membersRequired: 'At least one member is required'
      }
    },

    aliases: {
      title: 'Aliases',
      create: 'Create Alias',
      errorLoading: 'Failed to load aliases',
      empty: 'No aliases found',
      emptyHint: 'Create an alias to get started',
      active: 'Active',
      inactive: 'Inactive',
      delete: 'Delete',
      createTitle: 'Create Alias',
      creating: 'Creating...',
      createSubmit: 'Create',
      createSuccess: 'Alias created',
      createError: 'Failed to create alias',
      deleteTitle: 'Delete Alias',
      deleteConfirm: 'Delete',
      deleteWarning: 'This action cannot be undone. The alias will be permanently deleted.',
      deleteSuccess: 'Alias deleted',
      deleteError: 'Failed to delete alias',
      edit: 'Edit',
      editTitle: 'Edit Alias',
      editSubmit: 'Save',
      updating: 'Saving...',
      updateSuccess: 'Alias updated',
      updateError: 'Failed to update alias',
      columns: {
        email: 'Email',
        targets: 'Targets',
        status: 'Status',
        actions: 'Actions'
      },
      fields: {
        name: 'Alias Name',
        targets: 'Targets',
        targetsPlaceholder: "user{'@'}domain.com\nother{'@'}domain.com",
        targetsHint: 'One email address per line, comma or semicolon separated',
        active: 'Active'
      },
      validation: {
        nameRequired: 'Alias name is required',
        nameInvalid: 'Only letters, numbers, dots and hyphens allowed',
        targetsRequired: 'At least one target is required'
      }
    },

    create: {
      title: 'Create Mailbox',
      success: 'Mailbox created',
      error: 'Failed to create mailbox',
      creating: 'Creating...',
      submit: 'Create',
      fields: {
        name: 'Username',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        quota: 'Storage Quota'
      },
      validation: {
        nameRequired: 'Username is required',
        nameInvalid: 'Only letters, numbers, dots and hyphens allowed',
        passwordRequired: 'Password is required',
        passwordWeak: 'Password must be at least 8 characters',
        passwordMismatch: 'Passwords do not match'
      }
    },

    permissionLabels: {
      'mailbox:read': 'View Mailboxes',
      'mailbox:write': 'Manage Mailboxes',
      'mailbox:delete': 'Delete Mailboxes',
      'mailinglist:read': 'View Mailing Lists',
      'mailinglist:write': 'Manage Mailing Lists',
      'alias:read': 'View Aliases',
      'alias:write': 'Manage Aliases',
      'alias:delete': 'Delete Aliases'
    }
  }
} as const
